import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import '@/styles/site-office.css';
import PlaceByPhone, { placerTokenFor } from '@/components/tags/PlaceByPhone';
import TellTheOffice from '@/components/tags/TellTheOffice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create();

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US',
  { month: 'short', day: 'numeric', year: 'numeric' }) : '');
// DP276-M6 — the chip beside a button on a phone has room for a day, not a year.
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US',
  { month: 'short', day: 'numeric' }) : '');
const abs = (u) => (u && u.startsWith('/api') ? `${API}${u.replace(/^\/api/, '')}` : u);

const SPEC_ROWS = [
  ['Manufacturer', 'manufacturer'], ['Model', 'model_number'], ['Finish', 'finish'],
  ['Colour', 'color'], ['Dimensions', 'dimensions'], ['Notes', 'notes'],
];

/**
 * DP276-M3 — the spot tag: a blank until somebody standing in the room says
 * what is wrong. Claimed by a signed-in firm user (the page has no shell, so
 * it reads the token the app's axios client stores); after that anybody who
 * scans it reads the instruction and can tap Mark done once.
 */
const signedIn = () => !!(window.localStorage.getItem('accessToken')
  || window.localStorage.getItem('token'));

const SpotTag = ({ tag, code, onChange }) => {
  const navigate = useNavigate();
  // ST-4 — a firm blank has somewhere to go: the place flow in the app. A
  // scanner who is signed in is taken there and never sees this page.
  const openInApp = (tag.scope === 'firm' && !tag.project && tag.open_in_app) || null;
  const takeMeThere = openInApp && signedIn();
  useEffect(() => {
    if (takeMeThere) window.location.replace(openInApp);
  }, [takeMeThere, openInApp]);

  const [form, setForm] = useState({ instruction: '', room_id: '', trade: '' });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const ready = form.instruction.trim() && form.room_id && form.trade;

  const head = (
    <div className="kfs-page__hd">
      <span className="kfs-page__k">{tag.firm} · Spot tag {tag.spot_number}{tag.state === 'blank' ? '' : ' · punch item'}</span>
      <span className="kfs-page__t">{tag.state === 'blank' ? 'What is this?' : tag.title}</span>
      <span className="kfs-page__u">{tag.state === 'blank'
        ? `${tag.project} · unclaimed`
        : `${tag.project} · ${tag.room} · ${tag.trade} · ${tag.claimed_by_name} · ${fmtDate(tag.claimed_at)}`}</span>
    </div>
  );

  const claim = async () => {
    try {
      setBusy(true);
      const r = await axios.post(`${API}${tag.claim_url}/claim`, {
        instruction: form.instruction.trim(),
        room_id: form.room_id,
        trade: form.trade,
      });
      if (photo) {
        const body = new FormData();
        body.append('file', photo);
        await axios.post(`${API}${tag.claim_url}/photo`, body);
      }
      const fresh = await bare.get(`${API}/material-tags/${code}`);
      onChange(fresh.data || r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not save');
    } finally { setBusy(false); }
  };

  // DP276-M6 — sending the instruction to the trade that has to fix it. Only a
  // signed-in reader sees this; a scanner is a scanner.
  const [parties, setParties] = useState(null);
  const [pick, setPick] = useState('');
  const [sending, setSending] = useState(false);
  const projectId = (tag.send_url || '').split('/')[2] || '';

  useEffect(() => {
    if (!signedIn() || tag.state !== 'open' || !projectId) return;
    axios.get(`${API}/projects/${projectId}/memberships`)
      .then((r) => setParties((r.data?.memberships || [])
        .filter((p) => (p.status || 'active') === 'active'
          && (p.company_name || p.name))))
      .catch(() => setParties([]));
  }, [projectId, tag.state]);

  const match = (parties || []).find(
    (p) => (p.trade || '').toLowerCase() === (tag.trade || '').toLowerCase());
  const sentRows = tag.sent_to || [];

  const send = async (membershipId) => {
    try {
      setSending(true);
      const r = await axios.post(`${API}${tag.send_url}/send`,
        membershipId ? { membership_id: membershipId } : {});
      onChange({ ...tag, sent_to: r.data?.sent_to || sentRows });
      toast.success('Sent to the Site Office');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not send');
    } finally { setSending(false); }
  };

  const markDone = async () => {    try {
      const r = await bare.post(`${API}/material-tags/${code}/done`, {});
      onChange({ ...tag, state: 'done', done_at: r.data?.done_at });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not save');
    }
  };

  // ST-1 — a firm blank carries no project yet. ST-4 — scanning it is the way
  // in: signed in, the app opens straight to placing it; signed out, the page
  // asks for a sign-in and keeps the destination.
  if (tag.scope === 'firm' && !tag.project) {
    const placed = tag.state && tag.state !== 'blank';
    // ST-5 — a phone with no account places the tag itself. The man on the
    // ladder is the one standing in front of it; the sticker is his credential.
    const mine = !!placerTokenFor(code);
    // A tag placed from a phone reads back to anybody who scans it; a blank one
    // is there to be placed.
    if (!signedIn() && (tag.can_place || mine || tag.placer)) {
      if (!placed || mine) {
        return <PlaceByPhone tag={tag} code={code} />;
      }
      return (
        <div className="kso-wrap">
          <div className="kfs-page" data-testid="tag-placed-read">
            <div className="kfs-page__hd">
              <span className="kfs-page__k">{tag.firm} · Tag {tag.tag_number}</span>
              <span className="kfs-page__t">{tag.title || 'A placed tag.'}</span>
              <span className="kfs-page__u" data-testid="tag-placed-by">
                Placed by {tag.placer || 'somebody on site'} · {tag.state === 'done_pending'
                  ? 'marked done, waiting for the firm' : tag.state}
              </span>
            </div>
            {tag.note ? (
              <div className="kfs-page__bd" data-testid="tag-placed-note">{tag.note}</div>
            ) : null}
            {(tag.photos || []).length ? (
              <div className="kfs-page__bd">
                {tag.photos.map((u) => (
                  <img key={u} src={abs(u)} alt="" className="kso-ph"
                    data-testid="tag-placed-photo" />
                ))}
              </div>
            ) : null}
            <div className="kfs-hold" data-testid="tag-placed-hold">
              This tag was placed from another phone.
            </div>
            <TellTheOffice code={code} firm={tag.firm} />
          </div>
        </div>
      );
    }
    if (takeMeThere) {
      return (
        <div className="kso-wrap">
          <div className="kfs-page" data-testid="site-tag-opening">
            <div className="kfs-hold" data-testid="site-tag-blank-line">Opening KEY-MADE…</div>
          </div>
        </div>
      );
    }
    return (
      <div className="kso-wrap">
        <div className="kfs-page" data-testid="site-tag-blank">
          <div className="kfs-page__hd">
            <span className="kfs-page__k">{tag.firm} · Tag {tag.tag_number}</span>
            <span className="kfs-page__t">{placed ? 'A placed tag.' : 'A blank tag.'}</span>
          </div>
          <div className="kfs-hold" data-testid="site-tag-blank-line">
            {openInApp
              ? (placed ? 'Sign in to see this tag.' : 'A blank tag. Sign in to place it.')
              : (placed
                ? 'Open KEY-MADE on your phone to see this tag.'
                : 'A blank tag. Open KEY-MADE on your phone to place it.')}
          </div>
          {openInApp ? (
            <div className="kfs-page__bd">
              <a className="kfs-btn kso-btn brand" href={openInApp} data-testid="site-tag-open"
                onClick={(e) => {
                  // The sign-in page brings them back to this very path.
                  e.preventDefault();
                  const [pathname, search] = openInApp.split('?');
                  navigate('/login', { state: { from: { pathname, search: search ? `?${search}` : '' } } });
                }}>Open in KEY-MADE</a>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (tag.state === 'blank' && !signedIn()) {
    return (
      <div className="kso-wrap">
        <div className="kfs-page" data-testid="spot-tag-unclaimed-anon">
          {head}
          <div className="kfs-hold">{tag.firm} · {tag.project} — this tag is not yet in use.</div>
        </div>
      </div>
    );
  }

  if (tag.state === 'blank') {
    return (
      <div className="kso-wrap">
        <div className="kfs-page" data-testid="spot-tag-claim">
          {head}
          <div className="kfs-page__bd">
            <div className="kmt-form">
              <label style={{ gridColumn: '1 / -1' }}>Instruction
                <textarea className="kso-in" maxLength={500} value={form.instruction}
                  onChange={(e) => setForm({ ...form, instruction: e.target.value })}
                  placeholder="Shim the island end panel — gap at the floor is 3/8 in."
                  data-testid="spot-instruction" />
              </label>
              <label>Room
                <select className="kso-in" value={form.room_id}
                  onChange={(e) => setForm({ ...form, room_id: e.target.value })}
                  data-testid="spot-room">
                  <option value="">Which room?</option>
                  {(tag.room_options || []).map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </label>
              <label>Trade
                <select className="kso-in" value={form.trade}
                  onChange={(e) => setForm({ ...form, trade: e.target.value })}
                  data-testid="spot-trade">
                  <option value="">Who fixes it?</option>
                  {(tag.trade_options || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label style={{ gridColumn: '1 / -1' }}>Photo
                <input className="kso-in" type="file" accept="image/*" capture="environment"
                  onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                  data-testid="spot-photo" />
              </label>
              <div className="w">
                <button type="button" className="kso-btn brand" disabled={busy || !ready}
                  onClick={claim} data-testid="spot-save">Save — it becomes a punch item</button>
              </div>
            </div>
          </div>
          <div className="kso-foot">Claimed tags go straight onto the punch list · {tag.firm}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="kso-wrap">
      <div className="kfs-page" data-testid={`spot-tag-${tag.state}`}>
        {head}
        <div className="kfs-page__bd">
          <div className="kmt-where" data-testid="spot-tag-instruction">
            <span className="kfs-page__k">Instruction</span>
            <span className="l">{tag.instructions}</span>
          </div>
          {(tag.photos || []).length ? (
            <>
              <div className="kfs-group">Photo</div>
              <div className="kso-photos">
                {tag.photos.map((p, i) => (
                  <img key={p.url} src={abs(p.url)} alt=""
                    data-testid={`spot-photo-${i}`} />
                ))}
              </div>
            </>
          ) : null}
          <div className="kfs-group">Status</div>
          <div className="kso-row">
            <div className="t">{tag.state === 'done'
              ? `Done ${fmtDate(tag.done_at)}`
              : 'Open'}
              <span className="src">{tag.state === 'done' ? 'done' : `${tag.trade} field sheet`}</span>
            </div>
            {tag.state !== 'done' ? (
              <button type="button" className="kso-btn brand" onClick={markDone}
                data-testid="spot-mark-done">Mark done</button>
            ) : null}
          </div>
          {signedIn() && tag.state === 'open' ? (
            <div className="kso-row" data-testid="spot-send-row">
              <div className="t">Send to the trade
                {sentRows.length ? (
                  <><br /><span className="kso-tag ok" data-testid="spot-sent-tag">
                    Sent to {sentRows[sentRows.length - 1].name} · {fmtDay(sentRows[sentRows.length - 1].at)}
                  </span></>
                ) : null}
                {!sentRows.length && (match || parties === null) ? (
                  <span className="src">the instruction and the photo, in their Site Office</span>
                ) : null}
                {!match && parties !== null ? (
                  <select className="kso-in" value={pick}
                    onChange={(e) => setPick(e.target.value)}
                    data-testid="spot-send-pick">
                    <option value="">Pick a party…</option>
                    {parties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.company_name || p.name}{p.trade ? ` · ${p.trade}` : ''}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
              {match ? (
                <button type="button" className="kso-btn brand" disabled={sending}
                  onClick={() => send(match.id)} data-testid="spot-send-trade">
                  {sentRows.length ? 'Send again' : `Send to ${match.company_name || match.name}`}
                </button>
              ) : null}
              {!match && parties !== null ? (
                <button type="button" className="kso-btn brand"
                  disabled={!pick || sending} onClick={() => send(pick)}
                  data-testid="spot-send-trade">
                  {sentRows.length ? 'Send again' : 'Send'}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="kso-foot">Read-only except the one button · {tag.firm}</div>
      </div>
    </div>
  );
};

/**
 * DP276-M1 — the scanned tag.
 *
 * What the crew gets off the label on the carton: the room, the spot in the
 * room, what the thing is, the spec sheet — and one button, Mark installed.
 */
export default function MaterialTagPage() {
  const { code } = useParams();
  const [tag, setTag] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    bare.get(`${API}/material-tags/${code}`)
      .then((r) => { if (alive) setTag(r.data); })
      .catch(() => { if (alive) setError('This code is not in use.'); });
    return () => { alive = false; };
  }, [code]);

  const markInstalled = async () => {
    try {
      const r = await bare.post(`${API}/material-tags/${code}/installed`, {});
      setTag({ ...tag, state: 'installed', installed_at: r.data?.installed_at });
    } catch { setError('This code is not in use.'); }
  };

  if (error) {
    return (
      <div className="kso-wrap">
        <div className="kfs-page" data-testid="material-tag-unknown">
          <div className="kfs-hold">This code is not in use.</div>
        </div>
      </div>
    );
  }
  if (!tag) return <div className="kso-wrap"><p className="kso-hint">Loading…</p></div>;

  if (tag.kind === 'spot') {
    return <SpotTag tag={tag} code={code} onChange={setTag} />;
  }

  // MT-2 — the Tag Name is the headline; the product title sits beneath it.
  const head = (
    <div className="kfs-page__hd">
      <span className="kfs-page__k" data-testid="material-tag-head">
        {[tag.project, tag.project_code, tag.spec_num].filter(Boolean).join(' · ')}
      </span>
      <span className="kfs-page__t" data-testid="material-tag-name">
        {tag.tag_name || tag.title}
      </span>
      {tag.state === 'gone' ? null : (
        <span className="kfs-page__u" data-testid="material-tag-title">
          {tag.tag_name && tag.title !== tag.tag_name ? tag.title : tag.firm}
        </span>
      )}
    </div>
  );

  if (tag.state === 'gone') {
    return (
      <div className="kso-wrap">
        <div className="kfs-page" data-testid="material-tag-gone">
          {head}
          <div className="kfs-hold">{tag.firm} · {tag.project} — this tag is no longer in use.</div>
        </div>
      </div>
    );
  }

  const rows = SPEC_ROWS.filter(([, key]) => tag.spec?.[key]);

  return (
    <div className="kso-wrap">
      <div className="kfs-page" data-testid={`material-tag-${tag.state}`}>
        {head}
        <div className="kfs-page__bd">
          <div className="kmt-where" data-testid="material-tag-where">
            <span className="kfs-page__k">Goes in</span>
            <span className="r">{tag.room}</span>
            {tag.location ? <span className="l">{tag.location}</span> : null}
            {tag.instructions
              ? <span className="kfs-meta" data-testid="material-tag-note">{tag.instructions}</span>
              : null}
          </div>
          {/* PR-1c — where the carton is standing, read live: a label cannot
              move, and the thing it is stuck to does. */}
          {tag.now_at_where ? (
            <div className="kmt-at" data-testid="material-tag-now-at">
              <span className="kfs-page__k">Now at</span>
              <span className="r">
                {[tag.now_at_where, tag.now_at_location].filter(Boolean).join(' · ')}
                {tag.now_at_since ? ` · since ${fmtDay(tag.now_at_since)}` : ''}
              </span>
              {tag.at_where
                && (tag.at_where !== tag.now_at_where
                  || (tag.at_location || '') !== (tag.now_at_location || '')) ? (
                <span className="kfs-meta" data-testid="material-tag-printed-at">
                  {`(printed: ${[tag.at_where, tag.at_location].filter(Boolean).join(' · ')})`}
                </span>
              ) : null}
            </div>
          ) : null}
          {(tag.pictures || []).length ? (
            <>
              <div className="kfs-group">Pictures</div>
              <div className="kmt-pix" data-testid="material-tag-pictures">
                {tag.pictures.map((src, i) => (
                  <a key={src} href={abs(src)} target="_blank" rel="noreferrer"
                    data-testid={`material-tag-picture-${i}`}>
                    <img src={abs(src)} alt="" loading="lazy" />
                  </a>
                ))}
              </div>
            </>
          ) : null}
          {tag.drawing ? (
            <>
              <div className="kfs-group">Drawing</div>
              <div className="kso-row">
                <div className="t"><b>{tag.drawing.label}</b></div>
                <a className="kso-btn" href={abs(tag.drawing.url)} target="_blank" rel="noreferrer"
                  data-testid="material-tag-drawing">Open</a>
              </div>
            </>
          ) : null}
          {tag.spec ? (
            <>
              <div className="kfs-group">Spec sheet</div>
              <div className="kmt-spec">
                {tag.spec.image_url
                  ? <img className="img" src={abs(tag.spec.image_url)} alt=""
                      data-testid="material-tag-image" />
                  : <div className="img" />}
                <dl>
                  {rows.map(([label, key]) => (
                    <div key={key} style={{ display: 'contents' }}>
                      <dt>{label}</dt><dd>{tag.spec[key]}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              {(tag.attachments || []).map((a, i) => (
                <div className="kso-row" key={a.url}>
                  <div className="t">{a.name}<span className="src">attachment</span></div>
                  <a className="kso-btn" href={abs(a.url)} target="_blank" rel="noreferrer"
                    data-testid={`material-tag-file-${i}`}>Open</a>
                </div>
              ))}
            </>
          ) : null}
          <div className="kfs-group">Status</div>
          <div className="kso-row">
            <div className="t">
              {tag.state === 'installed'
                ? `Installed ${fmtDate(tag.installed_at)}`
                : tag.state === 'on_site'
                  ? `On site since ${fmtDate(tag.received_at || tag.state_at?.on_site)}`
                  : 'Tagged'}
              <span className="src">{tag.state === 'installed' ? 'done' : 'tagged at receiving'}</span>
            </div>
            {tag.state !== 'installed' ? (
              <button type="button" className="kso-btn brand" onClick={markInstalled}
                data-testid="tag-mark-installed">Mark installed</button>
            ) : null}
          </div>
        </div>
        <div className="kso-foot" data-testid="material-tag-foot">
          {[[tag.trade_prefix, tag.trade].filter(Boolean).join(' · '),
            `${tag.count_index} of ${tag.count_total}`,
            tag.scan_count ? `scanned ${tag.scan_count} times` : null,
            tag.last_scan_at ? `last ${fmtDate(tag.last_scan_at)}` : null]
            .filter(Boolean).join(' · ')}
        </div>
      </div>
    </div>
  );
}
